<template>
  <div class="product-label-question">
    <div>
      <img v-if="node.question.image !== null" :src="node.question.image" class="question-image" />
      <p v-if="node.prompt !== null" v-html="node.prompt"></p>
      <div v-html="node.question.instructions"></div>
      <v-file-input
        id="image-input"
        v-model="content"
        :label="node.question.product_label_prompt"
        accept="image/jpeg"
        capture="environment"
        outlined
        prepend-icon="mdi-camera"
        :hint="node.question.help_text"
        persistent-hint
        @change="onCaptureLabel"
      ></v-file-input>
    </div>

    <p v-if="isProcessing" class="processing">
      <img src="../assets/gear.gif" alt="processing..." class="busy" />
      Please wait, reading the label...
    </p>

    <div v-if="isCompleted" class="label">
      <p>
        <strong>Here's what we read from your label.</strong>
        Please check each value and unit, and correct anything that looks wrong.
      </p>

      <img
        v-if="labelImageUrl"
        :src="labelImageUrl"
        class="label-crop"
        alt="Your label photo"
      />

      <p v-if="localIngredients.length === 0" class="no-ingredients">
        We couldn't read any ingredients from this photo. You can still submit it.
      </p>

      <div v-for="group in groupedIngredients" :key="group.key" class="ingredient-group">
        <h4 class="group-title">{{ group.title }}</h4>
        <div
          v-for="row in group.rows"
          :key="row.id"
          class="ingredient"
          :class="{ edited: isRowEdited(row), unmapped: row.status === 'unmapped' }"
        >
          <div class="ingredient-head">
            <span class="ingredient-name">{{ row.verbatim_name }}</span>
            <span
              v-if="row.status === 'unmapped'"
              class="badge-unmapped"
              title="We captured this but haven't matched it to our list yet"
            >new</span>
            <span v-if="isRowEdited(row)" class="badge-edited">edited</span>
          </div>
          <div v-if="row.source_quote" class="source-quote">as printed: "{{ row.source_quote }}"</div>
          <div class="ingredient-fields">
            <v-text-field
              class="value-input"
              v-model="row.printed_value"
              label="Value"
              dense
              outlined
              hide-details
              inputmode="decimal"
              @input="onEditRow(row)"
            ></v-text-field>
            <v-select
              class="unit-input"
              v-model="row.printed_unit"
              :items="unitOptionsFor(row)"
              label="Unit"
              dense
              outlined
              hide-details
              @change="onEditRow(row)"
            ></v-select>
          </div>
        </div>
      </div>

      <p class="rating-prompt">
        To help us improve our automatic parsing, please let us know how well this label was parsed.
      </p>
      <v-select
        :items="ratingOptions"
        label="Select option..."
        item-text="label"
        item-value="id"
        v-model="rating"
        outlined
      ></v-select>
    </div>

    <div v-if="isError" class="error">
      There was an error while processing the label image. You can try again or just submit this
      label.
    </div>

    <div class="buttons">
      <v-btn
        class="submit-button"
        :disabled="!isValidSelection()"
        color="primary"
        @click="onAnswerQuestion"
      >
        {{ submitButton }}
      </v-btn>
      <v-btn v-if="node.skippable" class="submit-button" color="secondary" @click="onSkipQuestion">
        {{ skipButton }}
      </v-btn>
    </div>
  </div>
</template>

<script>
// Units offered in the confirm-UI dropdown. Mirrors participant.models.KNOWN_COMPOUND_UNITS
// (the backend validates a CHANGED unit against that set); '' allows a value with no unit.
const UNIT_OPTIONS = [
  '', 'mg', 'mcg', 'g', 'IU', '%', '%w/w', 'ppm', 'CFU', 'billion CFU', 'mL',
  'mg/serving', 'mg/capsule', 'mg/gummy', 'mg/piece', 'mg/unit', 'mg/g', 'mg/ml',
];

const CLASS_TITLES = {
  cannabinoid: 'Cannabinoids',
  terpene: 'Terpenes',
  other_ingredient: 'Other Ingredients',
};
const CLASS_ORDER = ['cannabinoid', 'terpene', 'other_ingredient'];

export default {
  name: 'ProductLabelQuestion',
  data() {
    return {
      content: null,
      rating: null,
      ratingOptions: [
        { id: 0, label: 'Everything looks correct' },
        { id: 1, label: 'A few missing fields or incorrect values' },
        { id: 2, label: 'Many missing fields or incorrect values' },
      ],
      unitOptions: UNIT_OPTIONS,
      // Editable copy of the captured rows -- never mutate the productLabel prop directly.
      localIngredients: [],
    };
  },
  computed: {
    isProcessing() {
      return this.productLabel != null && this.productLabel.status === 'processing';
    },
    isError() {
      return this.productLabel != null && this.productLabel.status === 'error';
    },
    isCompleted() {
      const pl = this.productLabel;
      if (pl == null || pl.status === 'processing' || pl.status === 'error') {
        return false;
      }
      // A terminal completed poll response carries `ingredients` (+ url/coa). Guard on a
      // completion marker so the empty {} initial state doesn't render the result view.
      return (
        Object.prototype.hasOwnProperty.call(pl, 'ingredients') ||
        Object.prototype.hasOwnProperty.call(pl, 'coa')
      );
    },
    labelImageUrl() {
      return (this.productLabel && this.productLabel.url) || null;
    },
    groupedIngredients() {
      return CLASS_ORDER.map((key) => ({
        key,
        title: CLASS_TITLES[key],
        rows: this.localIngredients.filter((r) => r.entity_class === key),
      })).filter((group) => group.rows.length > 0);
    },
  },
  watch: {
    productLabel: {
      immediate: true,
      handler(pl) {
        const rows = pl && Array.isArray(pl.ingredients) ? pl.ingredients : [];
        // Snapshot the original AI value/unit so the UI can flag human edits and the
        // backend flips provenance only on a real change.
        this.localIngredients = rows.map((r) => ({
          ...r,
          printed_value: r.printed_value == null ? '' : r.printed_value,
          printed_unit: r.printed_unit == null ? '' : r.printed_unit,
          _origValue: r.printed_value == null ? '' : r.printed_value,
          _origUnit: r.printed_unit == null ? '' : r.printed_unit,
        }));
      },
    },
  },
  methods: {
    isValidSelection() {
      // Block submit while extraction is still running (no rating selector shown yet).
      return this.content != null && !this.isProcessing;
    },
    isRowEdited(row) {
      return row.printed_value !== row._origValue || row.printed_unit !== row._origUnit;
    },
    onEditRow() {
      // Edits are tracked via isRowEdited (value/unit vs snapshot); the server flips
      // provenance on the confirm write. No state to mutate here beyond the v-model.
    },
    unitOptionsFor(row) {
      // Keep the row's as-extracted (possibly exotic) unit selectable even if it isn't
      // in the standard list, so the confirm-UI can display exactly what was captured.
      const current = row.printed_unit || '';
      if (current && !this.unitOptions.includes(current)) {
        return [current, ...this.unitOptions];
      }
      return this.unitOptions;
    },
    onCaptureLabel(file) {
      // Read the file straight off the @change payload, not `this.content` --
      // v-file-input's v-model update can lag behind @change firing, so
      // this.content is sometimes still the previous (or null) value here.
      // A cleared selection also fires @change with a falsy file; skip it.
      if (!file) {
        return;
      }
      this.$emit('capture', { node: this.node, image: file });
    },
    onAnswerQuestion() {
      // item-value="id" binds the numeric id itself, so `this.rating` IS the rating
      // (0/1/2) or null -- do NOT read `.id` off it. 0 is valid; guard null, not falsiness.
      const ratingId = this.rating == null ? null : this.rating;
      const profileId = this.productLabel && this.productLabel.formulation_profile_id;

      // Persist the rating + value/unit corrections onto the FormulationProfile. Best-
      // effort: the survey answer below also carries the rating, so a failed confirm
      // never loses it, and a failed profile write must not trap the participant.
      if (profileId && this.$store) {
        this.$store.dispatch('confirmLabel', {
          profileId,
          accuracyRating: ratingId,
          ingredients: this.localIngredients.map((r) => ({
            id: r.id,
            printed_value: r.printed_value,
            printed_unit: r.printed_unit,
          })),
        });
      }

      const result = {
        url: this.productLabel.url,
        coa: this.productLabel.coa,
        rating: ratingId,
        ingredients: this.localIngredients.map((r) => ({
          id: r.id,
          verbatim_name: r.verbatim_name,
          printed_value: r.printed_value,
          printed_unit: r.printed_unit,
          status: r.status,
        })),
      };
      this.$emit('answer', { node: this.node, value: ratingId, text: JSON.stringify(result) });
      this.content = null;
    },
    onSkipQuestion() {
      this.$emit('skip');
      this.content = null;
    },
  },
  props: {
    node: Object,
    productLabel: Object,
    submitButton: String,
    skipButton: String,
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/_variables.scss';

.product-label-question {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100%;
  padding: 1.5rem 1.5rem 1.5rem 1.5rem;

  .processing {
    margin-top: 10px;
  }

  .busy {
    height: 16px;
  }

  .question-image {
    display: block;
    max-width: 340px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 2rem;
  }

  .label-crop {
    display: block;
    max-width: 100%;
    max-height: 240px;
    margin: 0.5rem auto 1rem auto;
    border: 1px solid #ccc;
    border-radius: 4px;
  }

  .no-ingredients {
    font-style: italic;
    color: #666;
    margin: 0.5rem 0 1rem 0;
  }

  .ingredient-group {
    margin-bottom: 1rem;

    .group-title {
      font-size: 0.8rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #666;
      margin: 0.75rem 0 0.4rem 0;
    }
  }

  .ingredient {
    border: 1px solid #ddd;
    border-radius: 6px;
    padding: 0.5rem 0.6rem;
    margin-bottom: 0.5rem;

    &.edited {
      border-color: #2d0051;
      background-color: rgba(45, 0, 81, 0.03);
    }

    &.unmapped {
      border-left: 3px solid #ff8200;
    }

    .ingredient-head {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.4rem;
    }

    .ingredient-name {
      font-weight: 600;
    }

    .badge-unmapped,
    .badge-edited {
      font-size: 0.65rem;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      padding: 1px 6px;
      border-radius: 8px;
      color: #fff;
    }

    .badge-unmapped {
      background-color: #ff8200;
    }

    .badge-edited {
      background-color: #2d0051;
    }

    .source-quote {
      font-size: 0.75rem;
      color: #777;
      margin: 2px 0 6px 0;
    }

    .ingredient-fields {
      display: flex;
      gap: 0.5rem;

      .value-input {
        flex: 2;
      }

      .unit-input {
        flex: 1;
      }
    }
  }

  .rating-prompt {
    margin-top: 1rem;
  }

  .buttons {
    display: flex;
    width: 100%;
    flex-direction: row-reverse;
    justify-content: space-between;
    align-self: center;
  }
}

@media (min-width: $breakpoint-tablet) {
  .product-label-question {
    justify-content: flex-start;

    .buttons {
      margin-top: 3rem;
    }
  }
}
</style>
